import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import promoCodeService from "services/promoCode.services"; // Import the service
import styles from "./styles.module.scss";
import { FormButton, Pagination } from "reusableComponents";
import { selectAuthToken } from "store/auth/selectors";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import PromoCodeCard from "./PromoCodeCard"; // Import the PromoCodeCard component
import { ReusableConfirmationModal } from "modals";
import { useModal } from "context";

const PromoCodes = () => {
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal(); // Add closeModal here
  const { t } = useTranslation("global");
  const [promoCodes, setPromoCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationData, setPaginationData] = useState({
    total: 0,
    hasNextPage: false,
  });
  const perPage = 8;
  const accessToken = useSelector(selectAuthToken);

  // Fetch promo codes when the page changes
  useEffect(() => {
    const fetchPromoCodes = async () => {
      setIsLoading(true);
      try {
        const data = await promoCodeService.getAll(accessToken);
        setPromoCodes(data.data);
      } catch (error) {
        console.error("Error fetching promo codes:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPromoCodes();
  }, [currentPage, accessToken]);

  // Handle status change for promo code
  const handleStatusChange = async (id, active) => {
    try {
      await promoCodeService.update(id, { active }, accessToken);
      const data = await promoCodeService.getAll(accessToken);
      setPromoCodes(data.data);
    } catch (error) {
      console.error("Error updating promo code status:", error);
    }
  };

  const confirmStatusChange = (id, active) => {
    if (!active) {
      openModal({
        content: (
          <ReusableConfirmationModal
            message={t(
              "dashboard.promoCodesManagement.confirmationOfflineText"
            )}
            onConfirm={async () => {
              await handleStatusChange(id, active);
              closeModal(); // Close modal after status change
            }}
          />
        ),
        title: t("dashboard.spacesManagement.confirmationTitle"),
      });
    } else {
      handleStatusChange(id, active);
    }
  };

  // Handle deleting promo code
  const handleDelete = async (id) => {
    try {
      await promoCodeService.delete(id, accessToken);
      const data = await promoCodeService.getAll(accessToken);
      setPromoCodes(data.data);
      closeModal(); // Close modal after successful deletion
    } catch (error) {
      console.error("Error deleting promo code:", error);
    }
  };

  const confirmDelete = (id) => {
    openModal({
      content: (
        <ReusableConfirmationModal
          message={t("dashboard.promoCodesManagement.confirmationText")}
          onConfirm={() => handleDelete(id)}
        />
      ),
      title: t("dashboard.spacesManagement.confirmationTitle"),
    });
  };

  // Handle page change for pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <section
      className={`${styles.promoCodesWrapper} ${
        isLoading ? styles.loading : ""
      }`}
    >
      <div className={styles.header}>
        <h2 className={styles.dashboardTitle}>
          {t("dashboard.provider.promocodesManagement")}
        </h2>
        <div className={styles.addPromocodeBtnContainer}>
          <FormButton
            variant="gray"
            label={t("dashboard.promoCodes.create")}
            onClick={async () => navigate("/dashboard/promo-code/create")}
            className={styles.addButton}
          />
        </div>
      </div>
      {isLoading ? (
        <p></p>
      ) : (
        <div className={styles.promoList}>
          {promoCodes?.map((promo) => (
            <PromoCodeCard
              key={promo.id}
              promo={promo}
              handleDelete={confirmDelete}
              handleStatusChange={confirmStatusChange}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default PromoCodes;

